/**
 * @file index.tsx
 *
 * @fileoverview Renders the Oewa script in the page. It is based on GA4 component.
 */

import React, { useContext, useEffect, useRef } from 'react';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import Router from 'next/router';
import Script from 'next/script';
import { PathContextInterface } from '~/jobiqo/models/app';
import { PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { alterOewaConfigData } from '~/jobiqo/components/Slots/OewaAlterConfigSlot';

const usePageReloadIfOewaConsentGotRemoved = (currentOewaConsent) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise Oewa does not get excluded.
  const previousOewaConsent = useRef<boolean>(null);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousOewaConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.Oewa,
    );
  });

  // Compare if consent has changed from true to false.
  if (previousOewaConsent.current && !currentOewaConsent) {
    Router.reload();
  }
};

export const Oewa = () => {
  const userContext: UserContextInterface = useContext(UserContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const { APP } = useFrontendConfig();
  const OEWA_ACTIVE = !!APP.OEWA;
  const OEWA_DN = APP.OEWA?.DN;
  const OEWA_ST = APP.OEWA?.ST;
  const OEWA_CN = APP.OEWA?.CN;
  const OEWA_CP = APP.OEWA?.CP;
  const testing = process.env.testing;
  const staging = process.env.staging;

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.Oewa,
  );

  async function sendOewaData() {
    let oewa_config_data = {};
    let oewa_page_data = {};

    if (typeof window !== 'undefined' && (window as any)?.IOMm) {
      oewa_config_data = {
        st: OEWA_ST,
        dn: OEWA_DN,
        cn: OEWA_CN,
        mh: 5,
        i2: true,
      };
      oewa_page_data = {
        cp: OEWA_CP,
      };

      // Call a function named alterOewaConfigData to change the configuration and page data.
      // By default does no alterations.
      const alterData = await alterOewaConfigData(
        oewa_config_data,
        oewa_page_data,
        pathContext,
        userContext,
      );

      oewa_config_data = alterData.configData;
      oewa_page_data = alterData.pageData;

      // @ts-ignore
      IOMm('configure', oewa_config_data);
      // @ts-ignore
      IOMm('pageview', oewa_page_data);
    }
  }

  useEffect(() => {
    // Send data to Oewa for every page.
    if (OEWA_ACTIVE && isUserConsentNeededAndGiven && !testing && !staging) {
      // Add extra timeout to be sure script is loaded on initial page load.
      setTimeout(sendOewaData, 1000);
    }
  }, [pathContext, isUserConsentNeededAndGiven]);

  usePageReloadIfOewaConsentGotRemoved(isUserConsentNeededAndGiven);

  return OEWA_ACTIVE && isUserConsentNeededAndGiven && !testing && !staging ? (
    <>
      <Script
        id="Oewa-script-new"
        type="text/javascript"
        src={`https://${OEWA_DN}/iomm/latest/manager/base/es5/bundle.js`}
        async={true}
      ></Script>
    </>
  ) : null;
};

export default Oewa;
